import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Grid,
  TextField,
  Button,
} from "@mui/material";
import { ToastManager } from "../common/ToastContainer";
import { SESSION_AUTH } from "../../helpers/auth";
import { useParams } from "react-router-dom";
import { EVENTS_SERVICES } from "../../services";
import FullPageProgress from "../common/FullPageProgress";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";

const AddOrgazinationNewsModal = (props) => {
  const { accountId } = useParams();
  const { userSession, LOGOUT_USER_SESSION } = SESSION_AUTH();
  const { onClose, open, onUpdate } = props;

  const [loader, setLoader] = useState(false);
  const [title, setTitle] = useState("");
  const [keywords, setKeyWords] = useState("");
  const [summary, setSummary] = useState("");
  const [publishedAt, setPublishedAt] = useState(null);
  const [html, setHtml] = useState("");
  const [htmlMode, setHtmlMode] = useState(false); // toggle HTML mode

  const handleClose = () => onClose();

  const createNew = () => {
    if (!title) return ToastManager.addToast("Enter news title");
    if (!summary) return ToastManager.addToast("Enter news summary");
    if (!keywords) return ToastManager.addToast("Enter news keywords");
    if (!html) return ToastManager.addToast("Enter news content");

    setLoader(true);
    EVENTS_SERVICES.addOrgNews({
      userId: userSession.userId,
      title: title.trim(),
      category: "Business",
      summary: summary.trim(),
      accountId: accountId,
      readTime: 2,
      keywords: keywords.split(","),
      content: htmlToJson(html),
      publishedAt: publishedAt || "",
    })
      .then((data) => {
        if (data.code === 200) {
          ToastManager.addToast(data.message);
          onUpdate();
        } else if (data.code === 600) {
          LOGOUT_USER_SESSION();
          ToastManager.addToast(data.message);
        } else {
          ToastManager.addToast(data.message);
        }
      })
      .catch((error) => ToastManager.addToast(error.message))
      .finally(() => setLoader(false));
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      className="custom-dialog custom-dialog-full"
    >
      {loader && <FullPageProgress />}
      <DialogTitle className="text-center">Add News</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Title"
              placeholder="Enter news title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Summary"
              placeholder="Enter news summary"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Keywords"
              placeholder="Enter keywords with comma (,)"
              value={keywords}
              onChange={(e) => setKeyWords(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                sx={{ width: "100%" }}
                label="Published Date"
                format="MMM DD YYYY"
                placeholde="Enter published date"
                value={publishedAt ? dayjs(publishedAt) : null}
                onChange={(newValue) => {
                  setPublishedAt(newValue?.$d || null);
                }}
                maxDate={dayjs(new Date())}
                slotProps={{
                  field: {
                    clearable: true,
                    onClear: () => setPublishedAt(null),
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} className="managepro6ix-editor">
            <div style={{ textAlign: "right" }}>
              <Button
                variant="outlined"
                onClick={() => setHtmlMode(!htmlMode)}
                sx={{ mb: 1 }}
                size="small"
              >
                {htmlMode ? "Editor Mode" : "Insert Html"}
              </Button>
            </div>

            {htmlMode ? (
              <TextField
                multiline
                fullWidth
                minRows={8}
                value={html}
                onChange={(e) => setHtml(e.target.value)}
                placeholder="Edit HTML content"
              />
            ) : (
              <CKEditor
                editor={ClassicEditor}
                data={html}
                config={{
                  placeholder: "Enter content",
                  htmlSupport: {
                    allow: [
                      {
                        name: /.*/,
                        attributes: true,
                        classes: true,
                        styles: true,
                      },
                    ],
                  },
                  toolbar: [
                    "heading",
                    "|",
                    "bold",
                    "italic",
                    "link",
                    "bulletedList",
                    "numberedList",
                    "|",
                    "insertTable",
                    "|",
                    "undo",
                    "redo",
                  ],
                  table: {
                    contentToolbar: [
                      "tableColumn",
                      "tableRow",
                      "mergeTableCells",
                    ],
                  },
                }}
                onChange={(event, editor) => setHtml(editor.getData())}
              />
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className="custom-dialog-action">
        <button
          type="button"
          onClick={handleClose}
          className="custom-dialog-btn managepro6ix-btn managepro6ix-btn-secondary"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={createNew}
          className="custom-dialog-btn managepro6ix-btn managepro6ix-btn-primary"
        >
          Save
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default AddOrgazinationNewsModal;

// Converts HTML -> JSON
export const htmlToJson = (html) => [
  { type: "text", content: html, length: html.length, tagName: "div" },
];

// Converts JSON -> HTML
export const jsonToHtml = (blocks = []) =>
  blocks
    .map((block) => {
      if (block.type === "text") return block.content;
      if (block.type === "table") {
        return `
          <table style="border-collapse:collapse;width:100%" border="1">
            ${block.items
              .map(
                (row) =>
                  `<tr>${row.val
                    .map(
                      (cell) =>
                        `<td colspan="${cell.colspan}" rowspan="${cell.rowspan}" style="${cell.style || ""}">${cell.val
                          .map((v) => v.content)
                          .join("")}</td>`,
                    )
                    .join("")}</tr>`,
              )
              .join("")}
          </table>`;
      }
      return "";
    })
    .join("");
